/**
 * P167. The country pack behind the Partner Invite letter.
 *
 * The letter a licensed recruitment agency reads is country-specific: it names
 * the country, the licence that country's agencies actually hold ("Bangladesh
 * Recruitment License / RL Number"), and the workforce corridor being proposed.
 * Everything else in the letter is the same wherever it is sent, so the letter
 * BODY lives once as a template (publicPartnerRegisterTranslations.js) and this
 * file holds only the parts that genuinely change per country.
 *
 * Adding a country is ONE entry here plus one code in the backend's
 * PartnerCountries.All - nothing else in the flow is country-aware. The two
 * lists are deliberately validated against each other by this module's test, so
 * a card can never be created for a country whose letter does not exist (the
 * backend refuses an unknown code, and the public page treats a card it has no
 * letter for as not found).
 *
 * Bangladesh is the only entry today - the owner asked for Bangladesh first.
 */

export const PARTNER_COUNTRIES = {
  BD: {
    code: "BD",
    name: {
      en: "Bangladesh",
      th: "บังกลาเทศ",
      bn: "বাংলাদেশ",
      si: "බංග්ලාදේශය",
    },
    /**
     * The recruitment licence this country's agencies hold. This is the ONE
     * form field whose label is country-specific, and getting it right is what
     * makes the form read as written for the recipient rather than translated
     * at them.
     */
    licenseLabel: {
      en: "Bangladesh Recruitment License / RL Number",
      th: "เลขที่ใบอนุญาตจัดหางาน (RL Number) ของบังกลาเทศ",
      bn: "বাংলাদেশ রিক্রুটিং লাইসেন্স / আরএল নম্বর",
      si: "බංග්ලාදේශ බඳවා ගැනීමේ බලපත්‍රය / RL අංකය",
    },
    licensePlaceholder: "RL-1234",
    /** Why this country, in the sender's own words. */
    sourceCountryLine: {
      en: "Bangladesh is an important source country for skilled, semi-skilled, and general workers for international employment.",
      th: "บังกลาเทศเป็นประเทศต้นทางที่สำคัญของแรงงานฝีมือ แรงงานกึ่งฝีมือ และแรงงานทั่วไป สำหรับการจ้างงานระหว่างประเทศ",
      bn: "আন্তর্জাতিক কর্মসংস্থানের জন্য দক্ষ, আধা-দক্ষ ও সাধারণ কর্মী সরবরাহে বাংলাদেশ একটি গুরুত্বপূর্ণ উৎস দেশ।",
      si: "ජාත්‍යන්තර රැකියා සඳහා දක්ෂ, අර්ධ දක්ෂ සහ සාමාන්‍ය කම්කරුවන් සපයන වැදගත් මූලාශ්‍ර රටක් ලෙස බංග්ලාදේශය සැලකේ.",
    },
    corridorHeading: {
      en: "Building a Trusted Bangladesh–Thailand Workforce Corridor",
      th: "สร้างเส้นทางแรงงานบังกลาเทศ–ไทยที่เชื่อถือได้",
      bn: "বাংলাদেশ–থাইল্যান্ড আস্থাশীল কর্মী করিডোর গড়ে তোলা",
      si: "විශ්වාසනීය බංග්ලාදේශ–තායිලන්ත කම්කරු මාර්ගයක් ගොඩනැගීම",
    },
    /**
     * `{appName}` is substituted with the product name the server actually
     * serves (PublicPartnerInviteResponse.appName), so every mention in the
     * letter is spelled the same way. Any country added here should use the
     * same placeholder rather than writing the name out.
     */
    corridorLines: {
      en: [
        "We believe that Bangladesh and Thailand have significant potential for long-term workforce cooperation.",
        "Through {appName}, we aim to combine the strength of established Bangladeshi recruitment agencies with technology, structured verification, workforce demand, and coordinated back-office processes.",
      ],
      th: [
        "เราเชื่อว่าบังกลาเทศและไทยมีศักยภาพสูงในการร่วมมือด้านแรงงานในระยะยาว",
        "ผ่าน {appName} เราต้องการผสานความแข็งแกร่งของบริษัทจัดหางานบังกลาเทศที่มีประสบการณ์ เข้ากับเทคโนโลยี การตรวจสอบที่เป็นระบบ ความต้องการแรงงาน และการทำงานหลังบ้านที่ประสานกัน",
      ],
      bn: [
        "আমরা বিশ্বাস করি, দীর্ঘমেয়াদি কর্মী সহযোগিতার ক্ষেত্রে বাংলাদেশ ও থাইল্যান্ডের উল্লেখযোগ্য সম্ভাবনা রয়েছে।",
        "{appName}-এর মাধ্যমে আমরা বাংলাদেশের অভিজ্ঞ রিক্রুটিং এজেন্সিগুলোর সক্ষমতার সঙ্গে প্রযুক্তি, সুশৃঙ্খল যাচাই, কর্মী চাহিদা ও সমন্বিত ব্যাক-অফিস প্রক্রিয়া যুক্ত করতে চাই।",
      ],
      si: [
        "දිගුකාලීන කම්කරු සහයෝගිතාව සඳහා බංග්ලාදේශය සහ තායිලන්තය අතර සැලකිය යුතු හැකියාවක් ඇති බව අපි විශ්වාස කරමු.",
        "{appName} හරහා, බංග්ලාදේශයේ පළපුරුදු බඳවා ගැනීමේ ආයතනවල ශක්තිය තාක්ෂණය, ක්‍රමවත් සත්‍යාපනය, කම්කරු ඉල්ලුම සහ සම්බන්ධීකෘත පසුබිම් ක්‍රියාවලි සමඟ ඒකාබද්ධ කිරීමට අපි බලාපොරොත්තු වෙමු.",
      ],
    },
  },
};

/** Every country code the letter exists for, in display order. */
export const PARTNER_COUNTRY_CODES = Object.keys(PARTNER_COUNTRIES);

/**
 * Options for the Super Agent's create form. The label carries both names so
 * the sender picks the country they mean, whichever language they think in.
 */
export const PARTNER_COUNTRY_OPTIONS = PARTNER_COUNTRY_CODES.map((code) => ({
  value: code,
  label: `${PARTNER_COUNTRIES[code].name.th} (${PARTNER_COUNTRIES[code].name.en})`,
}));

/** The country pack for `code`, or null when this build has no letter for it. */
export function getPartnerCountry(code) {
  const key = String(code ?? "").trim().toUpperCase();
  return PARTNER_COUNTRIES[key] ?? null;
}

/**
 * The country's name in `language`, falling back to English (the letter's own
 * working language) rather than to the raw code - a reader should never be
 * shown "BD".
 */
export function partnerCountryName(code, language) {
  const country = getPartnerCountry(code);
  if (!country) {
    return "";
  }
  return country.name[language] ?? country.name.en;
}

/** Thai label for the backoffice list/inspector - "บังกลาเทศ (BD)", or "-". */
export function formatPartnerCountry(code) {
  const country = getPartnerCountry(code);
  if (!country) {
    return null;
  }
  return `${country.name.th} (${country.code})`;
}
